import { Box, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import moment from 'moment-timezone';
import { useDemoMode } from 'providers/demo-mode';
import { FC } from 'react';
import { LeadStatusChip } from './lead-status-chip';

// ============================================================================
// Styled Components
// ============================================================================

const ChatItemContainer = styled(Box)<{ selected: boolean }>(({ theme, selected }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderBottom: 'none',
  },
  transition: 'background-color 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    cursor: 'pointer',
  },
  backgroundColor: selected ? '#2e2e2e1a' : 'inherit',
}));

const TopRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

const RecentMessageRow = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'stretch',
}));

const MessageText = styled(Typography)(() => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
}));

// ============================================================================
// Component
// ============================================================================

export const ConversationItem: FC<{
  phoneNumber: string;
  status: string | null;
  lastMessage?: string;
  firstName: string | null;
  lastName: string | null;
  time: string;
  onClick: (id: string, campaignShortId?: string) => void;
  isSelected: boolean;
  campaignShortId?: string;
  campaignName?: string | null;
  showCampaign?: boolean;
}> = ({
  onClick,
  phoneNumber,
  status,
  lastMessage,
  firstName,
  lastName,
  isSelected,
  time,
  campaignShortId,
  campaignName,
  showCampaign,
}) => {
  const { getRandomFirstName, isInDemoMode } = useDemoMode();
  const relativeTime = moment(time).fromNow();

  return (
    <ChatItemContainer onClick={() => onClick(phoneNumber, campaignShortId)} selected={isSelected}>
      {!showCampaign && (
        <TopRow>
          {firstName && (
            <Typography>
              {getRandomFirstName(
                firstName && lastName ? `${firstName} ${lastName}` : firstName,
                phoneNumber,
              )}
            </Typography>
          )}
          {!isInDemoMode && <Typography variant="subtitle1">{phoneNumber}</Typography>}
        </TopRow>
      )}
      {showCampaign && (campaignShortId || campaignName) && (
        <Stack direction="row" spacing={1} sx={{ marginBottom: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
            {campaignName || campaignShortId}
          </Typography>
        </Stack>
      )}
      <RecentMessageRow>
        <MessageText variant="body1">{lastMessage}</MessageText>
      </RecentMessageRow>
      <Stack direction="row" spacing={2}>
        <LeadStatusChip status={status} />
        <Typography variant="caption" color="textSecondary">
          {relativeTime}
        </Typography>
      </Stack>
    </ChatItemContainer>
  );
};
